// Touch-Optimized 4-Digit On-Screen PIN Pad for Workshop Floor Tablets
import React from 'react';
import { Delete, RotateCcw } from 'lucide-react';

export interface PinPadProps {
  pin: string;
  onPinChange: (newPin: string) => void;
  onComplete?: (pin: string) => void;
  maxLength?: number;
  disabled?: boolean;
  error?: string;
}

export const PinPad: React.FC<PinPadProps> = ({
  pin,
  onPinChange,
  onComplete,
  maxLength = 4,
  disabled = false,
  error,
}) => {
  const triggerHaptic = () => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(20);
      } catch {
        // ignore
      }
    }
  };

  const handleDigitClick = (digit: string) => {
    if (disabled || pin.length >= maxLength) return;
    triggerHaptic();
    const nextPin = pin + digit;
    onPinChange(nextPin);
    if (nextPin.length === maxLength && onComplete) {
      onComplete(nextPin);
    }
  };

  const handleBackspace = () => {
    if (disabled || pin.length === 0) return;
    triggerHaptic();
    onPinChange(pin.slice(0, -1));
  };

  const handleClear = () => {
    if (disabled || pin.length === 0) return;
    triggerHaptic();
    onPinChange('');
  };

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div className="flex flex-col items-center select-none touch-manipulation max-w-[280px] mx-auto">
      {/* 4-Digit Indicator Dots */}
      <div className="flex items-center justify-center gap-4 mb-6">
        {Array.from({ length: maxLength }).map((_, idx) => {
          const isFilled = idx < pin.length;
          return (
            <div
              key={idx}
              className={`w-4 h-4 rounded-full transition-all duration-200 ${
                isFilled
                  ? 'bg-amber-500 shadow-sm scale-110'
                  : 'bg-slate-100 border-2 border-slate-300'
              }`}
            />
          );
        })}
      </div>

      {error && (
        <div className="text-xs font-bold text-rose-600 mb-4 text-center animate-pulse">
          {error}
        </div>
      )}

      {/* 3x4 Keypad Grid */}
      <div className="grid grid-cols-3 gap-3 w-full">
        {digits.map((num) => (
          <button
            key={num}
            type="button"
            disabled={disabled}
            onClick={() => handleDigitClick(num)}
            className="min-h-[56px] min-w-[56px] h-14 w-full rounded-2xl bg-white hover:bg-slate-50 active:bg-amber-500 active:text-white text-slate-800 font-mono text-2xl font-bold border border-slate-300 shadow-xs flex items-center justify-center transition-all duration-100 active:scale-95 disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
          >
            {num}
          </button>
        ))}

        {/* Clear Key */}
        <button
          type="button"
          disabled={disabled || pin.length === 0}
          onClick={handleClear}
          title="Clear"
          className="min-h-[56px] min-w-[56px] h-14 w-full rounded-2xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-600 border border-slate-300 shadow-xs flex items-center justify-center transition-all duration-100 active:scale-95 disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
        >
          <RotateCcw className="w-5 h-5" />
        </button>

        {/* Zero Key */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleDigitClick('0')}
          className="min-h-[56px] min-w-[56px] h-14 w-full rounded-2xl bg-white hover:bg-slate-50 active:bg-amber-500 active:text-white text-slate-800 font-mono text-2xl font-bold border border-slate-300 shadow-xs flex items-center justify-center transition-all duration-100 active:scale-95 disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
        >
          0
        </button>

        {/* Backspace Key */}
        <button
          type="button"
          disabled={disabled || pin.length === 0}
          onClick={handleBackspace}
          title="Backspace"
          className="min-h-[56px] min-w-[56px] h-14 w-full rounded-2xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-600 border border-slate-300 shadow-xs flex items-center justify-center transition-all duration-100 active:scale-95 disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
        >
          <Delete className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
